import React, { useState } from 'react';
import { PaymentMethod, PaymentDetailCreate } from '../../types/sale';

interface PaymentModalProps {
  total: number;
  onClose: () => void;
  onComplete: (payments: PaymentDetailCreate[]) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ total, onClose, onComplete }) => {
  const [payments, setPayments] = useState<PaymentDetailCreate[]>([
    { method: PaymentMethod.CASH, amount: total }
  ]);
  const [cashReceived, setCashReceived] = useState<number>(total);
  
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const change = cashReceived - total;
  const isValid = totalPaid >= total;
  
  const handleAddPayment = () => {
    setPayments([...payments, { method: PaymentMethod.CASH, amount: 0 }]);
  };
  
  const handleRemovePayment = (index: number) => {
    setPayments(payments.filter((_, i) => i !== index));
  };
  
  const handlePaymentChange = (index: number, field: keyof PaymentDetailCreate, value: any) => {
    const updated = [...payments];
    updated[index] = { ...updated[index], [field]: value };
    setPayments(updated);
  };
  
  const handleSubmit = () => {
    if (!isValid) {
      alert('Jumlah pembayaran kurang dari total!');
      return;
    }
    onComplete(payments);
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">Pembayaran</h2>
        </div>
        
        <div className="p-6 max-h-96 overflow-y-auto">
          {/* Total to Pay */}
          <div className="bg-blue-50 p-4 rounded mb-6">
            <div className="text-sm text-gray-600">Total yang harus dibayar:</div>
            <div className="text-3xl font-bold text-blue-600">
              Rp {total.toLocaleString()}
            </div>
          </div>
          
          {/* Payment Methods */}
          <div className="space-y-4 mb-6">
            {payments.map((payment, index) => (
              <div key={index} className="border rounded p-4">
                <div className="flex items-center gap-4 mb-3">
                  <select
                    value={payment.method}
                    onChange={(e) => handlePaymentChange(index, 'method', e.target.value as PaymentMethod)}
                    className="flex-1 px-3 py-2 border rounded"
                  >
                    <option value={PaymentMethod.CASH}>Cash</option>
                    <option value={PaymentMethod.TRANSFER}>Transfer</option>
                    <option value={PaymentMethod.QRIS}>QRIS</option>
                    <option value={PaymentMethod.EDC}>EDC</option>
                  </select>
                  
                  <input
                    type="number"
                    min="0"
                    value={payment.amount}
                    onChange={(e) => handlePaymentChange(index, 'amount', parseFloat(e.target.value) || 0)}
                    placeholder="Jumlah"
                    className="w-40 px-3 py-2 border rounded"
                  />
                  
                  {payments.length > 1 && (
                    <button
                      onClick={() => handleRemovePayment(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                {payment.method !== PaymentMethod.CASH && (
                  <input
                    type="text"
                    value={payment.reference || ''}
                    onChange={(e) => handlePaymentChange(index, 'reference', e.target.value)}
                    placeholder="Nomor Referensi / Approval Code"
                    className="w-full px-3 py-2 border rounded"
                  />
                )}
              </div>
            ))}
          </div>
          
          <button
            onClick={handleAddPayment}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded hover:border-blue-500 text-gray-600 hover:text-blue-500"
          >
            + Tambah Metode Pembayaran
          </button>
          
          {/* Cash Calculation (if using cash) */}
          {payments.some(p => p.method === PaymentMethod.CASH) && (
            <div className="mt-6 bg-gray-50 p-4 rounded">
              <div className="mb-3">
                <label className="block text-sm font-medium mb-2">Uang Diterima</label>
                <input
                  type="number"
                  min="0"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border rounded text-lg"
                />
              </div>
              
              <div className="flex justify-between items-center text-lg">
                <span className="font-medium">Kembalian:</span>
                <span className={`font-bold ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Rp {change.toLocaleString()}
                </span>
              </div>
            </div>
          )}
          
          {/* Summary */}
          <div className="mt-6 border-t pt-4">
            <div className="flex justify-between mb-2">
              <span>Total Tagihan:</span>
              <span className="font-bold">Rp {total.toLocaleString()}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Total Dibayar:</span>
              <span className={`font-bold ${totalPaid >= total ? 'text-green-600' : 'text-red-600'}`}>
                Rp {totalPaid.toLocaleString()}
              </span>
            </div>
            {totalPaid < total && (
              <div className="text-sm text-red-600">
                Kurang: Rp {(total - totalPaid).toLocaleString()}
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6 border-t flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 rounded font-medium"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid}
            className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Proses Pembayaran
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
